import React from 'react'
import { css, keyframes, createGlobalStyle } from 'styled-components'
import { getLuminance, lighten, darken, grayscale, parseToHsl, hsl, rgba as _rgba, mix as _mix } from 'polished'
import { Link } from 'react-router-dom'
import { pickBy } from 'lodash'
import theme from 'mocks/themes'
import { objectMap } from 'utils'

export const sizes = {
  desktop: 1280,
  tablet: 1025,
  mobile: 800,
  phone: 480,
}

export const isMobile = () => window.innerWidth <= sizes.mobile

export const isRetina = window.devicePixelRatio >= 2 ? '@2x' : ''

export const media = Object.keys(sizes).reduce((result, name, index, array) => {
  const maxWidth = `${sizes[name] / 16}em`
  const minWidth = `${(sizes[array[index + 1]] || 0) / 16}em`

  result[name] = (...args) => css`
    @media (max-width: ${maxWidth}) {
      ${css(...args)};
    }
  `

  result[`${name}Only`] = (...args) => css`
    @media (min-width: ${minWidth}) and (max-width: ${maxWidth}) {
      ${css(...args)};
    }
  `

  return result
}, {})

export const truncate = width =>
  css`
    width: ${width};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  `

export const rgba = (color, alpha) => (typeof color != 'function' ? _rgba(color, alpha) : p => _rgba(color(p), alpha))

export const mix = (w, color, bg) => (typeof color != 'function' ? _mix(w, color, bg) : p => _mix(w, color(p), bg(p)))

export const highlight = (value, color) =>
  (getLuminance(color) > getLuminance('#888') ? darken : lighten)(value, color)

export const contrast = (a, b, large) => {
  const [darker, lighter] = [0.05 + getLuminance(a), 0.05 + getLuminance(b)].sort()
  const ratio = Math.round((20 * lighter) / darker) / 20
  const score = ratio >= 7 ? 'AAA' : ratio >= 4.5 ? 'AA' : large && ratio >= 3 ? 'A' : ''
  return { score, ratio }
}

export const avatarBackground = ({ theme: { colors } }) => {
  const a = parseToHsl(colors.main)
  const b = parseToHsl(colors.mainInverted)
  return hsl(a.hue, (a.saturation + b.saturation) / 2, (a.lightness + 2 * b.lightness) / 3)
}

export const innerBackground = ({ theme: { colors } }) => {
  const { hue, saturation } = parseToHsl(colors.main)
  let { lightness } = parseToHsl(colors.mainBackground)
  lightness = lightness + (lightness > 0.5 ? -1 : 1) * 0.05
  return hsl(hue, saturation, lightness)
}

export const colors = objectMap(theme.colors, (_, key) => props => props.theme.colors[key])

export const fonts = {
  url: p => p.theme.fonts.url,
  body: p => p.theme.fonts.names.body,
  title: p => p.theme.fonts.names.title,
  monospace: '"Roboto Mono", monospace',
}

const radiusSteps = [0, 0.3, 0.6, 1.2, 4, 10] // only 0, 1, 2 used atm

export const radiuses = {
  small: p => `${radiusSteps[p.theme.roundness]}rem`,
  medium: p => `${radiusSteps[p.theme.roundness] * 2}rem`,
  large: p => `${radiusSteps[p.theme.roundness] * 3}rem`,
}

export const input = {
  outer: css`
    font-size: 1.2rem;
    width: 100%;
    min-height: 3em;
    box-sizing: border-box;
    background-color: transparent;
    color: ${colors.textMain};
    ${media.mobile`
      font-size: 1.6rem;
    `}
  `,
  padding: '0.8rem',
  border: p => `0.2rem solid ${p.theme.colors.divider}`,
  borderRadius: radiuses.medium,
}
input.common = css`
  ${input.outer};
  padding: ${input.padding};
  border: ${input.border};
  border-radius: ${input.borderRadius};
`

// When using custom components such as styled(Link) instead of styled.a
// The props are being handed down from Link to the DOM, triggering a warning
// This filters out some unwanted props (for styling only)
const StyledLink = props => <Link {...pickBy(props, key => !['inverted', 'placeholder'].includes(key))} />

// styled.Link = styled(props => <Link {...pickBy(props, key => !['inverted', 'placeholder'].includes(key))} />);

export { createGlobalStyle, css, keyframes, lighten, darken, grayscale, StyledLink as Link }
